import { useState } from "react";
import styled from "styled-components";
import { Mail, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Container } from "../layout/Container";
import { subscribeNewsletter } from "../../services/contactServices";

const Section = styled.section`
  background: hsl(${({ theme }) => theme.colors.navDark});
  padding: 2.5rem 0;
`;

const Center = styled.div`
  max-width: min(100%, 56rem);
  margin: 0 auto;
  text-align: center;
`;

const IconWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.accent});
`;

const Title = styled.h3`
  font-size: clamp(1.25rem, 1.05rem + 0.75vw, 1.625rem);
  font-weight: 700;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  margin: 0 0 0.5rem;
`;

const Desc = styled.p`
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.6);
  margin: 0 0 1.25rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: min(100%, 36rem);
  margin: 0 auto;
  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const Input = styled.input`
  flex: 1;
  border: 1px solid hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.2);
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 0.625rem 1rem;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  background: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.05);
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
  outline: none;
  &::placeholder {
    color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.4);
  }
  &:focus {
    box-shadow: 0 0 0 2px hsl(${({ theme }) => theme.colors.primary} / 0.4);
  }
`;

const SubmitBtn = styled.button`
  background: hsl(${({ theme }) => theme.colors.accent});
  color: hsl(${({ theme }) => theme.colors.accentForeground});
  padding: 0.625rem 1.5rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  font-weight: 600;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }
`;

const FinePrint = styled.p`
  font-size: clamp(0.8125rem, 0.78rem + 0.15vw, 0.9375rem);
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.4);
  margin: 0.75rem 0 0;
`;

export default function NewsletterBanner() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await subscribeNewsletter(email.trim());
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("newsletter.error"));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Section>
        <Container>
          <Center>
            <IconWrap>
              <CheckCircle size={40} strokeWidth={2} />
            </IconWrap>
            <Title>{t("newsletter.subscribed")}</Title>
            <Desc>{t("newsletter.subscribedSub")}</Desc>
          </Center>
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container>
        <Center>
          <IconWrap>
            <Mail size={40} strokeWidth={2} />
          </IconWrap>
          <Title>{t("newsletter.title")}</Title>
          <Desc>{t("newsletter.subtitle")}</Desc>
          <Form onSubmit={handleSubmit}>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("newsletter.placeholder")}
            />
            <SubmitBtn type="submit" disabled={loading}>
              {loading ? "…" : t("newsletter.subscribe")}
            </SubmitBtn>
          </Form>
          {error ? (
            <FinePrint style={{ color: "hsl(348 60% 70%)" }}>{error}</FinePrint>
          ) : null}
          <FinePrint>{t("newsletter.noSpam")}</FinePrint>
        </Center>
      </Container>
    </Section>
  );
}
